import { getTestAttributes } from '@common/utils/tests';
import * as React from 'react';
import { I18n } from "react-redux-i18n";
import cx from 'classnames';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@common/components/modal';

import { Button } from '@common/components/Button';
import { Flex } from '@common/components/Flex';
import { TreeGetIdArgs } from '@common/components/Tree';
import FormatManagement from '@common/components/FormatManagement';
import { FolderFormatsDto, FolderTemplateFormatId, FolderTemplateFormatName, FolderTemplateOption, FormatFoldersDto } from '@common/dto/folder-settings';
import { CreateFormatFolderParams, FolderFormatConfig } from '@common/actions/formats';

import './formats.styles.scss';

type FormatsProps = {
  onCloseFormatModal: () => void;
  getFormatFolders: (type: keyof FolderTemplateFormatId) => Promise<FormatFoldersDto[] | undefined>;
  getFolderFormats: (type: keyof FolderTemplateFormatId, queries?: FolderFormatConfig['queries']) => Promise<FolderFormatsDto[] | undefined>;
  onSubmit: (data: { defaultFormatIds: Partial<FolderTemplateFormatId>, defaultFormatNames: Partial<FolderTemplateFormatName> }) => void;
  templateOptions: Partial<FolderTemplateOption>;
  defaultFormatIds: Partial<FolderTemplateFormatId>;
  defaultFormatNames: Partial<FolderTemplateFormatName>;
  createFolder: (type: keyof FolderTemplateFormatId, data: CreateFormatFolderParams) => Promise<void>;
  deleteFolder: (type: keyof FolderTemplateFormatId, id: number) => Promise<void>;
  deleteFormat: (type: keyof FolderTemplateFormatId, id: number) => Promise<void>;
};

type FormatListItem = {
  ids: {
    value: string;
    editBtn: string;
    clearBtn: string;
  };
  label: string;
  idKey: keyof FolderTemplateFormatId;
  nameKey: keyof FolderTemplateFormatName;
  conditionKey: keyof FolderTemplateOption;
}

export const Formats = ({
  templateOptions,
  defaultFormatIds,
  defaultFormatNames,
  onCloseFormatModal,
  getFormatFolders,
  getFolderFormats,
  createFolder,
  deleteFolder,
  deleteFormat,
  onSubmit
}: FormatsProps) => {

  const [openedManagementType, setOpenedManagementType] = React.useState<keyof FolderTemplateFormatId>();

  const [formatFolders, setFormatFolders] = React.useState<FolderFormatsDto[]>();
  const [folderFormats, setFolderFormats] = React.useState<FolderFormatsDto[]>();
  const [selectedFormat, setSelectedFormat] = React.useState({
    templateOptions,
    defaultFormatIds,
    defaultFormatNames
  });

  const formats = React.useMemo((): FormatListItem[] => [
    {
      label: I18n.t('Individual Datasheet'),
      ids: {
        value: 'tx-template-formats-wnd-ids-format',
        editBtn: 'fp-template-formats-wnd-ids-format-edit',
        clearBtn: 'fp-template-formats-wnd-ids-format-delete',
      },
      idKey: 'defaultIdsFormatId',
      nameKey: 'defaultIdsFormatName',
      conditionKey: 'tempIds'
    },
    {
      label: I18n.t('Pedigree Datasheet'),
      idKey: 'defaultPdsFormatId',
      ids: {
        value: 'tx-template-formats-wnd-pds-format',
        editBtn: 'fp-template-formats-wnd-pds-format-edit',
        clearBtn: 'fp-template-formats-wnd-pds-format-delete',
      },
      nameKey: 'defaultPdsFormatName',
      conditionKey: 'tempPds'
    },
    {
      label: I18n.t('Subtext Format'),
      idKey: 'defaultSubtextFormatId',
      ids: {
        value: 'tx-template-formats-wnd-subtext-format',
        editBtn: 'fp-template-formats-wnd-subtext-format-edit',
        clearBtn: 'fp-template-formats-wnd-subtext-format-delete',
      },
      nameKey: 'defaultSubtextFormatName',
      conditionKey: 'tempSubtext'
    },
    {
      label: I18n.t('Symbols Format'),
      idKey: 'defaultSymbolsFormatId',
      ids: {
        value: 'tx-template-formats-wnd-symbols-format',
        editBtn: 'fp-template-formats-wnd-symbols-format-edit',
        clearBtn: 'fp-template-formats-wnd-symbols-format-delete',
      },
      nameKey: 'defaultSymbolsFormatName',
      conditionKey: 'tempSymbols'
    },
    {
      label: I18n.t('Pedigree Fields'),
      idKey: 'defaultPedDropFormatId',
      ids: {
        value: 'tx-template-formats-wnd-ped-format',
        editBtn: 'fp-template-formats-wnd-ped-format-edit',
        clearBtn: 'fp-template-formats-wnd-ped-format-delete',
      },
      nameKey: 'defaultPedDropFormatName',
      conditionKey: 'tempPedDropFields'
    },
    {
      label: I18n.t('Smart Header Format'),
      idKey: 'defaultHeaderFormatId',
      ids: {
        value: 'tx-template-formats-wnd-header-format',
        editBtn: 'fp-template-formats-wnd-header-format-edit',
        clearBtn: 'fp-template-formats-wnd-header-format-delete',
      },
      nameKey: 'defaultHeaderFormatName',
      conditionKey: 'tempHeader'
    },
    {
      label: I18n.t('Smart Footer Format'),
      idKey: 'defaultFooterFormatId',
      ids: {
        value: 'tx-template-formats-wnd-footer-format',
        editBtn: 'fp-template-formats-wnd-footer-format-edit',
        clearBtn: 'fp-template-formats-wnd-footer-format-delete',
      },
      nameKey: 'defaultFooterFormatName',
      conditionKey: 'tempFooter'
    },
  ], []);

  const getFolders = React.useCallback(async () => {
    const data = await getFormatFolders(openedManagementType!);
    setFormatFolders(data);
  }, [getFormatFolders, setFormatFolders, openedManagementType]);

  const getFormats = React.useCallback(async (folderId: FormatFoldersDto['id']) => {
    const data = await getFolderFormats(openedManagementType!, { folderId });
    setFolderFormats(data);
  }, [setFolderFormats, getFolderFormats, openedManagementType]);

  const handleCloseManagement = React.useCallback(() => {
    setOpenedManagementType(undefined);
    setFormatFolders(undefined);
    setFolderFormats(undefined);
  }, [setOpenedManagementType, setFormatFolders, setFolderFormats]);

  const onFormatChange = React.useCallback(({ id, name }: FolderFormatsDto) => {
    const selectedFormat = formats.find(({ idKey }) => idKey === openedManagementType)!;

    setSelectedFormat((prev) => ({
      ...prev,
      defaultFormatIds: {
        ...prev.defaultFormatIds,
        [selectedFormat.idKey]: id,
      },
      defaultFormatNames: {
        ...prev.defaultFormatNames,
        [selectedFormat.nameKey]: name,
      },

    }));
    handleCloseManagement();
  }, [openedManagementType, formats, setSelectedFormat, handleCloseManagement]);

  const onDelete = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const { type }: Partial<{ type: keyof FolderTemplateFormatId }> = e.currentTarget.dataset;
    const selectedFormat = formats.find(({ idKey }) => idKey === type)!;

    setSelectedFormat((prev) => ({
      ...prev,
      defaultFormatIds: {
        ...prev.defaultFormatIds,
        [selectedFormat.idKey]: null,
      },
      defaultFormatNames: {
        ...prev.defaultFormatNames,
        [selectedFormat.nameKey]: null,
      },
    }));
  }, [onFormatChange, setSelectedFormat, formats]);

  const handleOnEdit = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const { type }: Partial<{ type: keyof FolderTemplateFormatId }> = e.currentTarget.dataset;
    setOpenedManagementType(type);
  }, [setOpenedManagementType]);


  const handleSubmit = React.useCallback(() => {
    const { defaultFormatIds, defaultFormatNames } = selectedFormat;
    onSubmit({ defaultFormatIds, defaultFormatNames });
    onCloseFormatModal();
  }, [selectedFormat, onSubmit]);

  const handleResetFormats = React.useCallback(() => {
    setFolderFormats(undefined);
  }, [setFolderFormats]);

  const handleFolderCreate = React.useCallback(({ name, parentId }: CreateFormatFolderParams) => {
    return createFolder(openedManagementType!, {
      name,
      parentId: parentId || 0
    }).then(getFolders);
  }, [getFormats, getFolders, createFolder, openedManagementType]);

  const handleFolderDelete = React.useCallback((id: number) => {
    deleteFolder(openedManagementType!, id).then(getFolders);
  }, [getFolders, deleteFolder, openedManagementType]);

  const handleFormatDelete = React.useCallback((folderId: number, formatId: number) => {
    deleteFormat(openedManagementType!, formatId).then(() => {
      getFormats(folderId);
    });
  }, [getFormats, deleteFormat, openedManagementType]);

  return (
    <Modal
      show
      bsSize="medium"
      bsClass="bootstrap modal"
      onHide={onCloseFormatModal}
      onEscapeKeyDown={onCloseFormatModal}
      animation={false}
    >
      <ModalHeader closeButton onHide={onCloseFormatModal}>
        <ModalTitle
          {...getTestAttributes({ id: 'lbl-template-formats-wnd-header' })}
        >
          {I18n.t('Template Formats')}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <div className="FolderManagement__formats">
          {openedManagementType && (
            <FormatManagement
              getId={{
                modalTitle: 'lbl-datasheet-format-folder-picker-header',
                creatingFolderName: 'tx-datasheet-add-folder-wnd-folder-name',
                creatingFolderCancelBtn: 'lbl-datasheet-add-folder-wnd-cancel',
                creatingFolderCreateBtn: 'lbl-datasheet-add-folder-wnd-save',
                field: ({ rowIndex }: { rowIndex: number; colIndex: number }) => `bsgrid-${rowIndex}-0`,
                creatingFolderItem: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined 
                  ? `folder-create-${ownIndex}` 
                  : `folder-create-child-${data.id}`,
                creatingFolderCollapseItem: ({ parentIndex, ownIndex }: TreeGetIdArgs) => parentIndex === undefined 
                  ? `folder-create-${ownIndex}-exp-icon`
                  : `folder-create-child-${ownIndex}-exp-icon`,
                folder: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined 
                  ? `folder-${ownIndex}` 
                  : `folder-child-${data.id}`,
                collapseIcon: ({ parentIndex, ownIndex }: TreeGetIdArgs) => parentIndex === undefined 
                  ? `folder-${ownIndex}-exp-icon`
                  : `folder-child-${ownIndex}-exp-icon`,
                newFolderBtn: 'btn-datasheet-format-folder-picker-new-folder',
                deleteFolderBtn: 'btn-datasheet-format-folder-picker-delete-folder',
                deleteFormatBtn: 'btn-datasheet-format-folder-picker-delete-format',
                cancelBtn: 'btn-datasheet-format-folder-picker-cancel',
                submitBtn: 'btn-datasheet-format-folder-picker-ok',
              }}
              onFormatDelete={handleFormatDelete}
              onFolderCreate={handleFolderCreate}
              onFolderDelete={handleFolderDelete}
              resetFormats={handleResetFormats}
              folders={formatFolders}
              formats={folderFormats}
              getFolders={getFolders}
              getFormats={getFormats}
              close={handleCloseManagement}
              onSubmit={onFormatChange}
            />
          )}
          {formats.map((format, index) => {
            const disableField = !(selectedFormat?.['templateOptions']?.[format.conditionKey] ?? false);

            return (
              <div
                key={format.idKey}
                className={cx("FolderManagement__formats__item", { ['FolderManagement__formats__item__disabled']: disableField })}
              >
                <span
                  {...getTestAttributes({ id: `lbl-template-formats-wnd-format-${index}` })}
                  className="FolderManagement__formats__item__label"
                >
                  {format.label}:
                </span>
                <div
                  className="FolderManagement__formats__item__value"
                  {...getTestAttributes({ id: format.ids.value })}
                >
                  {selectedFormat?.['defaultFormatNames']?.[format.nameKey]}
                </div>
                <button
                  {...getTestAttributes({ id: format.ids.editBtn })}
                  data-id={selectedFormat?.['defaultFormatIds']?.[format.idKey]}
                  data-name={selectedFormat?.['defaultFormatNames']?.[format.nameKey]}
                  data-type={format.idKey}
                  onClick={handleOnEdit}
                  className="FolderManagement__formats__item__action-btn"
                  disabled={disableField}
                >
                  <i className="fa fa-edit FolderManagement__formats__item__edit-icon" />
                </button>
                <button
                  className="FolderManagement__formats__item__action-btn"
                  {...getTestAttributes({ id: format.ids.clearBtn })}
                  data-type={format.idKey}
                  onClick={onDelete}
                  disabled={disableField}
                >
                  <i className="fa fa-times FolderManagement__formats__item__delete-icon" />
                </button>
              </div>
            );
          })}
        </div>
      </ModalBody>
      <ModalFooter>
        <Flex justify="flex-end">
          <Button
            onClick={onCloseFormatModal}
            {...getTestAttributes({ id: 'btn-template-formats-wnd-ped-cancel' })}
          >
            {I18n.t('Cancel')}
          </Button>
          <Button
            bsStyle="primary"
            onClick={handleSubmit}
            {...getTestAttributes({ id: 'btn-template-formats-wnd-ped-ok' })}
          >
            {I18n.t('OK')}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  )
}